import styles from "../../../styles/components/UI/Custom/Title.module.css";
import React, { useContext, useEffect, useMemo, useRef, useState } from "react";
import NotificationsIcon from "@mui/icons-material/Notifications";
import DashboardIcon from "@mui/icons-material/Dashboard";
import CloseIcon from "@mui/icons-material/Close";

import PersonIcon from "@mui/icons-material/Person";
import InfoOutlinedIcon from "@mui/icons-material/InfoOutlined";
import TrendingUpOutlinedIcon from "@mui/icons-material/TrendingUpOutlined";
import AttachMoneyOutlinedIcon from "@mui/icons-material/AttachMoneyOutlined";
import PaymentOutlinedIcon from "@mui/icons-material/PaymentOutlined";
import ChatBubbleOutlineOutlinedIcon from "@mui/icons-material/ChatBubbleOutlineOutlined";
import HistoryOutlinedIcon from "@mui/icons-material/HistoryOutlined";

import { LangContext } from "../../../context/LangContext";
import { ThemeContext } from "../../../context/ThemeContext";
import { useNotifications } from "../../../hooks/useNotifications";
import { useNavigate } from "react-router-dom";
import { Notification as Toast } from "../Notifications";

const translations = {
    ru: {
        notificationsHeader: "Уведомления",
        noNotifications: "Нет новых уведомлений.",
        readAll: "Прочитать все",
        viewAll: "Все уведомления",
    },
    en: {
        notificationsHeader: "Notifications",
        noNotifications: "No new notifications.",
        readAll: "Mark all as read",
        viewAll: "All notifications",
    },
};

function iconByNotification(n) {
    const action = String(n?.action || "").toLowerCase();
    const type = String(n?.related_entity_type || "").toLowerCase();

    if (action.startsWith("user.") || action.includes("registered") || type === "lead") return <PersonIcon />;
    if (action.includes("deposit_method")) return <PaymentOutlinedIcon />;
    if (
        action.includes("transaction") ||
        action.includes("deposit") ||
        action.includes("withdraw") ||
        action.includes("payment")
    )
        return <AttachMoneyOutlinedIcon />;
    if (action.startsWith("order.") || action.includes("open_deal") || action.includes("deal") || type === "order")
        return <TrendingUpOutlinedIcon />;
    if (action.includes("comment") || action.includes("chat") || type === "chat") return <ChatBubbleOutlineOutlinedIcon />;
    if (action.includes("history") || action.includes("changed") || action.includes("updated")) return <HistoryOutlinedIcon />;

    return <InfoOutlinedIcon />;
}

function fullLeadName(notification) {
    return String(notification?.lead_name || "").trim();
}

export const Title = ({ h2 = "", rightAddon = null, inlineNotifications = false }) => {
    const { langSelected } = useContext(LangContext);
    const { theme } = useContext(ThemeContext);
    const t = translations[langSelected] || translations.ru;

    const [isOpen, setIsOpen] = useState(false);
    const [toast, setToast] = useState(null);
    const notificationDropdownRef = useRef(null);
    const notificationButtonRef = useRef(null);
    const audioContextRef = useRef(null);
    const toastFrameRef = useRef(null);
    const previousInlineIdsRef = useRef([]);
    const navigate = useNavigate();

    const { unreadCount, hasNew, items, loadingList, load, markRead, markAllRead } = useNotifications();
    const [dismissedInlineIds, setDismissedInlineIds] = useState([]);
    const [removingInlineIds, setRemovingInlineIds] = useState([]);
    const [freshInlineIds, setFreshInlineIds] = useState([]);

    const toggleNotifications = (event) => {
        if (notificationButtonRef.current && notificationButtonRef.current.contains(event.target)) {
            setIsOpen((v) => !v);
        } else if (!isOpen) {
            setIsOpen(true);
        }
    };

    // Load list when opening dropdown (lazy)
    useEffect(() => {
        if (!isOpen) return;
        load({ perPage: 20, unreadOnly: false });
    }, [isOpen, load]);

    useEffect(() => {
        if (!inlineNotifications) {
            return;
        }

        load({ perPage: 20, unreadOnly: false });
    }, [inlineNotifications, load]);

    useEffect(() => {
        const handleClickOutside = (event) => {
            if (
                notificationDropdownRef.current &&
                !notificationDropdownRef.current.contains(event.target) &&
                notificationButtonRef.current &&
                !notificationButtonRef.current.contains(event.target)
            ) {
                setIsOpen(false);
            }
        };

        document.addEventListener("mousedown", handleClickOutside);
        return () => document.removeEventListener("mousedown", handleClickOutside);
    }, []);

    const notifications = useMemo(() => (Array.isArray(items) ? items : []), [items]);
    const inlineNotificationsItems = useMemo(
        () => notifications
            .filter((notification) => !notification.read_at)
            .filter((notification) => !dismissedInlineIds.includes(notification.id))
            .slice(0, 5),
        [notifications, dismissedInlineIds]
    );

    useEffect(() => {
        if (!inlineNotifications) {
            return;
        }

        const currentIds = inlineNotificationsItems.map((notification) => notification.id);
        const previousIds = previousInlineIdsRef.current;
        const nextFreshIds = currentIds.filter((id) => !previousIds.includes(id));

        if (nextFreshIds.length > 0) {
            setFreshInlineIds((prev) => Array.from(new Set([...prev, ...nextFreshIds])));

            window.setTimeout(() => {
                setFreshInlineIds((prev) => prev.filter((id) => !nextFreshIds.includes(id)));
            }, 450);
        }

        previousInlineIdsRef.current = currentIds;
    }, [inlineNotifications, inlineNotificationsItems]);

    const openNotification = async (notification, closeDropdown = false) => {
        if (!notification?.url) {
            return;
        }

        try {
            await markRead(notification.id);
        } catch {
            // keep navigation behavior stable even if read API fails
        }

        if (closeDropdown) {
            setIsOpen(false);
        }

        navigate(notification.url);
    };

    const handleDismissInlineNotification = async (notificationId) => {
        try {
            await markRead(notificationId);
        } catch {
            // keep dismiss behavior even if read API fails
        }

        setRemovingInlineIds((prev) => (prev.includes(notificationId) ? prev : [...prev, notificationId]));

        window.setTimeout(() => {
            setDismissedInlineIds((prev) => (prev.includes(notificationId) ? prev : [...prev, notificationId]));
            setRemovingInlineIds((prev) => prev.filter((id) => id !== notificationId));
        }, 220);
    };

    const playSoftNotificationSound = () => {
        try {
            const AudioCtx = window.AudioContext || window.webkitAudioContext;
            if (!AudioCtx) return;

            if (!audioContextRef.current) {
                audioContextRef.current = new AudioCtx();
            }

            const ctx = audioContextRef.current;
            if (ctx.state === "suspended") {
                ctx.resume().catch(() => {});
            }

            const playTone = (frequency, startAt, duration, maxVolume) => {
                const oscillator = ctx.createOscillator();
                const gain = ctx.createGain();

                oscillator.type = "triangle";
                oscillator.frequency.setValueAtTime(frequency, startAt);

                gain.gain.setValueAtTime(0.0001, startAt);
                gain.gain.exponentialRampToValueAtTime(maxVolume, startAt + 0.03);
                gain.gain.exponentialRampToValueAtTime(0.0001, startAt + duration);

                oscillator.connect(gain);
                gain.connect(ctx.destination);

                oscillator.start(startAt);
                oscillator.stop(startAt + duration + 0.02);
            };

            const now = ctx.currentTime + 0.01;
            playTone(523.25, now, 0.14, 0.02); // C5
            playTone(659.25, now + 0.11, 0.16, 0.018); // E5
        } catch {
            // ignore audio errors
        }
    };

    useEffect(() => {
        const handleNewNotification = (event) => {
            const message = String(event?.detail?.message || "").trim();
            if (!message) return;

            // Force-replace current toast with the latest notification.
            setToast(null);
            if (toastFrameRef.current) {
                cancelAnimationFrame(toastFrameRef.current);
            }
            toastFrameRef.current = requestAnimationFrame(() => {
                setToast({
                    id: `${Date.now()}-${Math.random()}`,
                    message,
                });
            });

            playSoftNotificationSound();
        };

        window.addEventListener("crm.notification.created", handleNewNotification);
        return () => {
            window.removeEventListener("crm.notification.created", handleNewNotification);
            if (toastFrameRef.current) {
                cancelAnimationFrame(toastFrameRef.current);
            }
        };
    }, []);

    return (
        <>
            {toast && <Toast key={toast.id} message={toast.message} type="info" />}

            <div className={styles.sectionHeader}>
                <div className={`${styles.sectionTitleContainer} ${theme === "dark" ? styles.darkTitleContainer : ""}`}>
                    <div className={styles.mainTitleIcon}>
                        <DashboardIcon fontSize="inherit" />
                    </div>

                    <h2 className={styles.sectionTitle}>{h2}</h2>
                </div>

                <div className={styles.sectionHeaderCenter}>
                    {inlineNotifications && (
                        <div className={`${styles.inlineNotificationsRow} ${theme === "dark" ? styles.darkInlineNotificationsRow : ""}`}>
                            {loadingList && inlineNotificationsItems.length === 0 ? (
                                <div className={`${styles.inlineNotificationsEmpty} ${theme === "dark" ? styles.darkInlineNotificationsEmpty : ""}`}>...</div>
                            ) : inlineNotificationsItems.length > 0 ? (
                                inlineNotificationsItems.map((notification) => {
                                    const isUnread = !notification.read_at;
                                    const isRemoving = removingInlineIds.includes(notification.id);
                                    const isFresh = freshInlineIds.includes(notification.id);
                                    const leadName = fullLeadName(notification);

                                    return (
                                        <div
                                            key={notification.id}
                                            className={[
                                                styles.inlineNotificationItem,
                                                theme === "dark" ? styles.darkInlineNotificationItem : "",
                                                isUnread ? styles.inlineNotificationItemUnread : "",
                                                isFresh ? styles.inlineNotificationItemFresh : "",
                                                isRemoving ? styles.inlineNotificationItemRemoving : "",
                                            ].join(" ")}
                                            role={notification.url ? "button" : undefined}
                                            tabIndex={notification.url ? 0 : undefined}
                                            onClick={() => openNotification(notification)}
                                            onKeyDown={(event) => {
                                                if (!notification.url) return;
                                                if (event.key === "Enter" || event.key === " ") {
                                                    event.preventDefault();
                                                    openNotification(notification);
                                                }
                                            }}
                                        >
                                            <div className={`${styles.inlineNotificationIcon} ${theme === "dark" ? styles.darkInlineNotificationIcon : ""}`}>
                                                {iconByNotification(notification)}
                                            </div>
                                            <div className={styles.inlineNotificationBody}>
                                                <span className={styles.inlineNotificationMessage}>
                                                    {notification.message}
                                                </span>
                                            </div>
                                            {(leadName || notification.message) && (
                                                <div className={`${styles.inlineNotificationTooltip} ${theme === "dark" ? styles.darkInlineNotificationTooltip : ""}`}>
                                                    {leadName && (
                                                        <div className={styles.inlineNotificationTooltipRow}>
                                                            <span className={styles.inlineNotificationTooltipLabel}>Lead:</span>
                                                            <span className={styles.inlineNotificationTooltipValue}>{leadName}</span>
                                                        </div>
                                                    )}
                                                    {notification.message && (
                                                        <div className={styles.inlineNotificationTooltipRow}>
                                                            <span className={styles.inlineNotificationTooltipLabel}>Message:</span>
                                                            <span className={styles.inlineNotificationTooltipValue}>{notification.message}</span>
                                                        </div>
                                                    )}
                                                </div>
                                            )}
                                            <button
                                                type="button"
                                                className={`${styles.inlineNotificationClose} ${theme === "dark" ? styles.darkInlineNotificationClose : ""}`}
                                                onClick={(event) => {
                                                    event.stopPropagation();
                                                    handleDismissInlineNotification(notification.id);
                                                }}
                                                aria-label="Close notification"
                                            >
                                                <CloseIcon fontSize="inherit" />
                                            </button>
                                        </div>
                                    );
                                })
                            ) : (
                                <div className={`${styles.inlineNotificationsEmpty} ${theme === "dark" ? styles.darkInlineNotificationsEmpty : ""}`}>
                                    {t.noNotifications}
                                </div>
                            )}
                        </div>
                    )}

                    {rightAddon && (
                        <div
                            className={`${styles.sectionHeaderAddon} ${theme === "dark" ? styles.darkSectionHeaderAddon : ""}`}
                            onClick={(e) => e.stopPropagation()}
                        >
                            {rightAddon}
                        </div>
                    )}
                </div>

                <div
                    className={`${styles.sectionNotificationBox} ${theme === "dark" ? styles.darkNotificationBox : ""}`}
                    onClick={toggleNotifications}
                    ref={notificationButtonRef}
                >
                    <div className={styles.sectionNotificationIcon}>
                        <NotificationsIcon fontSize="inherit" />
                        {hasNew && <div className={`${styles.notificationDot} ${theme === "dark" ? styles.darkNotificationDot : ""}`} />}
                    </div>

                    {isOpen && (
                        <div
                            className={`${styles.notificationDropdown} ${theme === "dark" ? styles.darkNotificationDropdown : ""}`}
                            ref={notificationDropdownRef}
                            onClick={(e) => e.stopPropagation()}
                        >
                            <div className={`${styles.notificationHeader} ${theme === "dark" ? styles.darkNotificationHeader : ""}`}>
                                {t.notificationsHeader}
                                {unreadCount > 0 ? ` (${unreadCount})` : ""}
                            </div>

                            <div className={styles.notificationList}>
                                {loadingList ? (
                                    <p className={`${styles.noNotifications} ${theme === "dark" ? styles.darkNoNotifications : ""}`}>...</p>
                                ) : notifications.length > 0 ? (
                                    <>
                                        {notifications.map((n) => {
                                            const isUnread = !n.read_at;

                                            return (
                                                <div
                                                    key={n.id}
                                                    className={[
                                                        styles.notificationItem,
                                                        theme === "dark" ? styles.darkNotificationItem : "",
                                                        isUnread ? styles.notificationItemUnread : "",
                                                        theme === "dark" && isUnread ? styles.darkNotificationItemUnread : "",
                                                    ].join(" ")}
                                                    role={n.url ? "button" : undefined}
                                                    tabIndex={n.url ? 0 : undefined}
                                                    onClick={() => openNotification(n, true)}
                                                    onKeyDown={(e) => {
                                                        if (!n.url) return;
                                                        if (e.key === "Enter" || e.key === " ") {
                                                            e.preventDefault();
                                                            openNotification(n, true);
                                                        }
                                                    }}
                                                >
                                                    <div className={`${styles.notificationIcon} ${theme === "dark" ? styles.darkNotificationIcon : ""}`}>
                                                        {iconByNotification(n)}
                                                    </div>

                                                    <div className={styles.notificationTextContainer}>
                                                        <p className={`${styles.notificationMessage} ${theme === "dark" ? styles.darkNotificationMessage : ""}`}>
                                                            {n.message}
                                                        </p>

                                                        <p className={`${styles.notificationTime} ${theme === "dark" ? styles.darkNotificationTime : ""}`}>
                                                            {n.created_at || ""}
                                                            {!n.read_at ? <span className={styles.unreadDotInline} /> : null}
                                                        </p>
                                                    </div>
                                                </div>
                                            );
                                        })}
                                    </>
                                ) : (
                                    <p className={`${styles.noNotifications} ${theme === "dark" ? styles.darkNoNotifications : ""}`}>
                                        {t.noNotifications}
                                    </p>
                                )}
                            </div>

                            {notifications.length > 0 && (
                                <button
                                    className={styles.clearButton}
                                    onClick={() => {
                                        markAllRead();
                                        setIsOpen(false);
                                    }}
                                >
                                    {t.readAll}
                                </button>
                            )}

                            <button
                                className={styles.clearButton}
                                onClick={() => {
                                    setIsOpen(false);
                                    navigate("/notifications");
                                }}
                            >
                                {t.viewAll}
                            </button>
                        </div>
                    )}
                </div>
            </div>
        </>
    );
};
